import type { AlarmRecipient, AlarmRecipientFields } from '@ctcells/api'
import type { Alarm, ModuleSnapshot } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

/**
 * 알림 수신자 — 화면 모델(`AlarmRecipientsSection` · 알람 상세 「발송」 칸).
 *
 * 🔴 **「받은 것 위에 고친 칸만」**(CLAUDE.md PUT 절) — 고치기 본문은 받은 값과 «달라진 칸만» 싣는다(`recipientPatch`).
 *    서버 PATCH 는 병합이라(`든 칸만`) 다 보내도 오늘은 안전하지만, 모르는 칸을 새로 지어 덮는 습관을 들이지 않는다.
 * 🔴 **주소는 다시 보이지 않는다** — 응답은 `url_set` 만 준다. 칸은 늘 빈 칸으로 열리고, 비워 두면 «그대로»(본문에서 뺀다).
 * 🔴 **「전부」 = `null`** — `sources` · `error_codes` 가 `null`(응답에서는 키째 없음 · `exclude_none`)이면 거르지 않는다.
 *    하나라도 고르면 배열이다. 빈 배열은 만들지 않는다(서버가 «아무것도 안 받음»으로 읽을 수 있는 모양을 짓지 않는다).
 * 🔴 **후보는 손 목록이 아니다**(정본 `07` §4.7) — 출처 = 모듈 목록 + 알람 목록에 실제로 나온 `source` · 코드 = 알람 목록에 나온
 *    `error_code`. 직접 적기도 받는다. 카탈로그 밖 코드는 서버가 422 로 거절한다 — 프론트가 카탈로그를 베껴 미리 막지 않는다.
 */

export type RecipientKind = AlarmRecipient['kind']
export type RecipientSeverity = AlarmRecipient['min_severity']
export type RecipientRole = AlarmRecipient['role']

export const RECIPIENT_KINDS: readonly RecipientKind[] = ['webhook', 'slack']
export const RECIPIENT_SEVERITIES: readonly RecipientSeverity[] = ['WARN', 'ERROR', 'CRITICAL']
export const RECIPIENT_ROLES: readonly RecipientRole[] = ['all', 'escalation', 'backup']

/** 창의 칸 — 화면이 고치는 값 전부. 주소는 늘 빈 칸에서 시작한다 */
export interface RecipientDraft {
  name: string
  kind: RecipientKind
  url: string
  enabled: boolean
  min_severity: RecipientSeverity
  role: RecipientRole
  /** `null` = 전부 */
  sources: string[] | null
  /** `null` = 전부 */
  error_codes: string[] | null
}

/** 새 수신자 창의 처음 값 — 서버 기본값과 같다(`enabled=true` · `role=all`). 등급은 사람이 고르지만 자리는 가장 낮은 값 */
export function emptyDraft(): RecipientDraft {
  return {
    name: '',
    kind: 'webhook',
    url: '',
    enabled: true,
    min_severity: 'WARN',
    role: 'all',
    sources: null,
    error_codes: null,
  }
}

/** 받은 수신자 → 창의 처음 값. 🔴 주소는 빈 칸(응답에 없다) */
export function draftOf(recipient: AlarmRecipient): RecipientDraft {
  return {
    name: recipient.name,
    kind: recipient.kind,
    url: '',
    enabled: recipient.enabled,
    min_severity: recipient.min_severity,
    role: recipient.role,
    sources: recipient.sources == null ? null : [...recipient.sources],
    error_codes: recipient.error_codes == null ? null : [...recipient.error_codes],
  }
}

/** 목록 칸 정리 — 앞뒤 공백 · 빈 값 · 겹침을 뺀다. 남은 것이 없으면 `null`(= 전부) */
export function normalizeList(values: readonly string[] | null): string[] | null {
  if (values === null) return null
  const out = [...new Set(values.map((value) => value.trim()).filter((value) => value !== ''))]
  return out.length === 0 ? null : out
}

function sameList(a: readonly string[] | null | undefined, b: readonly string[] | null): boolean {
  const left = a ?? null
  if (left === null || b === null) return left === b
  return left.length === b.length && left.every((value, index) => value === b[index])
}

/** 등록 본문 — 창의 칸 전부(주소 포함 · 서버가 필수로 본다) */
export function createFields(draft: RecipientDraft): AlarmRecipientFields {
  return {
    name: draft.name.trim(),
    kind: draft.kind,
    url: draft.url.trim(),
    enabled: draft.enabled,
    min_severity: draft.min_severity,
    role: draft.role,
    sources: normalizeList(draft.sources),
    error_codes: normalizeList(draft.error_codes),
  }
}

/**
 * 고치기 본문 — 🔴 **받은 값과 달라진 칸만**. 주소는 적었을 때만 싣는다(비우면 «그대로»).
 * 달라진 칸이 없으면 빈 객체 — 부르는 쪽이 보내지 않는다.
 */
export function recipientPatch(
  original: AlarmRecipient,
  draft: RecipientDraft,
): AlarmRecipientFields {
  const out: AlarmRecipientFields = {}
  const name = draft.name.trim()
  if (name !== original.name) out.name = name
  if (draft.kind !== original.kind) out.kind = draft.kind
  if (draft.enabled !== original.enabled) out.enabled = draft.enabled
  if (draft.min_severity !== original.min_severity) out.min_severity = draft.min_severity
  if (draft.role !== original.role) out.role = draft.role
  const sources = normalizeList(draft.sources)
  if (!sameList(original.sources, sources)) out.sources = sources
  const codes = normalizeList(draft.error_codes)
  if (!sameList(original.error_codes, codes)) out.error_codes = codes
  const url = draft.url.trim()
  if (url !== '') out.url = url
  return out
}

export type RecipientErrorKey = 'name' | 'url'

/**
 * 칸별 입력 오류 — 추가 · 저장을 누를 때 잰다(eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」).
 * 🔴 형식 판정(주소 꼴 · 코드 카탈로그)은 서버가 한다 — 여기선 «비었나»만. 고치기 창의 빈 주소는 «그대로»라 오류가 아니다
 */
export function recipientDraftErrors(
  draft: RecipientDraft,
  creating: boolean,
): Partial<Record<RecipientErrorKey, string | undefined>> {
  const out: Partial<Record<RecipientErrorKey, string | undefined>> = {}
  if (draft.name.trim() === '') out.name = t('validation.nameRequired')
  if (creating && draft.url.trim() === '') out.url = t('alarmRecipients.reason.urlRequired')
  return out
}

/** 「거름」 칸 한 줄 — 출처 · 코드 각자 「전부」 또는 n 개 */
export function filterSummary(recipient: Pick<AlarmRecipient, 'sources' | 'error_codes'>): string {
  const part = (values: readonly string[] | null | undefined) =>
    values == null ? t('alarmRecipients.filter.all') : String(values.length)
  return t('alarmRecipients.filter.summary', {
    sources: part(recipient.sources),
    codes: part(recipient.error_codes),
  })
}

function uniqueSorted(values: Iterable<string | null | undefined>): string[] {
  const kept = [...values].filter(
    (value): value is string => typeof value === 'string' && value !== '',
  )
  return [...new Set(kept)].sort((a, b) => a.localeCompare(b))
}

/** 출처 후보 — 모듈 id + 알람에 실제로 나온 `source`(코어 발원 `orchestrator` 등). 🔴 옛 행의 종류 값 `*` 는 뺀다(와일드카드로 읽지 않는다) */
export function sourceCandidates(
  modules: readonly Pick<ModuleSnapshot, 'module_id'>[],
  alarms: readonly Pick<Alarm, 'source'>[],
): string[] {
  return uniqueSorted([
    ...modules.map((module) => module.module_id),
    ...alarms.map((alarm) => alarm.source).filter((source) => source !== '*'),
  ])
}

/** 코드 후보 — 알람에 실제로 나온 `error_code` */
export function codeCandidates(alarms: readonly Pick<Alarm, 'error_code'>[]): string[] {
  return uniqueSorted(alarms.map((alarm) => alarm.error_code))
}

/** 422 `REQUEST_VALIDATION_FAIL` 의 `context.reason` — 서버가 칸 이름 · 규칙을 문자열 목록으로 준다. 모양이 다르면 빈 목록 */
export function rejectReasons(error: unknown): string[] {
  const context = (error as { context?: { reason?: unknown } } | null)?.context
  const reason = context?.reason
  if (Array.isArray(reason))
    return reason.filter((item): item is string => typeof item === 'string')
  return typeof reason === 'string' ? [reason] : []
}

/* ───────────────────────── 알람 상세 「발송」 칸 ───────────────────────── */

/** 발송 채널 머리 — `recipient:<recipient_id>`(계약 `Alarm.dispatch[].channel`) */
export const RECIPIENT_CHANNEL_PREFIX = 'recipient:'

export type AlarmDispatch = NonNullable<Alarm['dispatch']>[number]

/**
 * 발송 채널의 이름.
 * - 수신자 목록을 받았으면(admin) `recipient:<id>` → 그 이름 · 목록에 없는 id 는 「지운 수신자」
 * - 목록을 못 받았으면(admin 아님 · 403) **채널 원문 그대로** — 「지운 수신자」로 단정하지 않는다
 */
export function dispatchChannelName(
  channel: string,
  recipients: readonly Pick<AlarmRecipient, 'recipient_id' | 'name'>[] | undefined,
): string {
  if (recipients === undefined || !channel.startsWith(RECIPIENT_CHANNEL_PREFIX)) return channel
  const id = channel.slice(RECIPIENT_CHANNEL_PREFIX.length)
  const found = recipients.find((recipient) => recipient.recipient_id === id)
  return found === undefined ? t('alarmRecipients.dispatch.deleted') : found.name
}
